import * as S from './styles';
import Icon, { IconType } from 'components/Icon';
import { Loader } from 'components/Widget/Loader';
import React, {
	useRef,
	useState,
	forwardRef,
	useImperativeHandle,
	useEffect,
	useCallback,
} from 'react';
import { unthemed } from 'tokens';
import { trackLearningAction } from 'analytics';

interface VideoPlayerProps {
	src: string;
	onEnded?: () => void;
	onTimeUpdate?: () => void;
	onPlay?: () => void;
	onPause?: () => void;
	onCanPlay?: () => void;
}

export interface VideoPlayerRef {
	currentTime: number;
	duration: number;
	play: () => Promise<void>;
	pause: () => void;
	setCurrentTime: (time: number) => Promise<void>;
}

const VideoPlayer = forwardRef<VideoPlayerRef, VideoPlayerProps>(
	({ src, onEnded, onTimeUpdate, onPlay, onPause, onCanPlay }, ref) => {
		const videoRef = useRef<HTMLVideoElement>(null);
		const [isLoading, setIsLoading] = useState(true);
		const [isPlaying, setIsPlaying] = useState(false);
		const [currentTime, setCurrentTime] = useState(0);
		const [duration, setDuration] = useState(0);
		const [volume, setVolume] = useState(1);
		const [isDragging, setIsDragging] = useState(false);
		const [showVolumeSlider, setShowVolumeSlider] = useState(false);

		const handleLoadStart = () => {
			setIsLoading(true);
		};

		const handleCanPlay = () => {
			setIsLoading(false);
			if (videoRef.current) {
				setDuration(videoRef.current.duration);
			}
			onCanPlay?.();
		};

		const handleError = () => {
			setIsLoading(false);
			// For development, show a placeholder when video fails to load
			if (videoRef.current) {
				videoRef.current.style.display = 'none';
			}
		};

		const handleTimeUpdate = () => {
			if (videoRef.current && !isDragging) {
				const videoTime = videoRef.current.currentTime;
				// Only update state if the video time has changed meaningfully
				if (Math.abs(videoTime - currentTime) > 0.5 /* half a second */) {
					setCurrentTime(videoTime);
				}
			}
			onTimeUpdate?.();
		};

		const handlePlay = () => {
			setIsPlaying(true);
			onPlay?.();
		};

		const handlePause = () => {
			setIsPlaying(false);
			onPause?.();
		};

		const handleEnded = () => {
			setIsPlaying(false);
			// Don't reset currentTime to 0 - keep it at the end
			// setCurrentTime(0);
			onEnded?.();
		};

		const togglePlayPause = () => {
			if (videoRef.current) {
				if (isPlaying) {
					trackLearningAction('video', 'pause');
					videoRef.current.pause();
				} else {
					trackLearningAction('video', 'play');
					videoRef.current.play();
				}
			}
		};

		const handleTimelineChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			trackLearningAction('video', 'seek');
			const newTime = parseFloat(e.target.value);
			setCurrentTime(newTime);
			if (videoRef.current) {
				videoRef.current.currentTime = newTime;
			}
		};

		const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			const newVolume = parseFloat(e.target.value);
			setVolume(newVolume);
			if (videoRef.current) {
				videoRef.current.volume = newVolume;
			}
		};

		const toggleVolumeSlider = () => {
			setShowVolumeSlider(!showVolumeSlider);
		};

		const closeVolumeSlider = useCallback(() => {
			setShowVolumeSlider(false);
		}, []);

		const toggleFullscreen = () => {
			if (videoRef.current) {
				if (document.fullscreenElement) {
					document.exitFullscreen();
				} else {
					trackLearningAction('video', 'enter_fullscreen');
					videoRef.current.requestFullscreen();
				}
			}
		};

		// Close volume slider when clicking outside
		useEffect(() => {
			if (showVolumeSlider) {
				const handleClickOutside = (event: MouseEvent) => {
					const target = event.target as Element;
					if (!target.closest('[data-volume-controls]')) {
						closeVolumeSlider();
					}
				};

				document.addEventListener('mousedown', handleClickOutside);
				return () => document.removeEventListener('mousedown', handleClickOutside);
			}
		}, [showVolumeSlider, closeVolumeSlider]);

		const formatTime = (time: number) => {
			const minutes = Math.floor(time / 60);
			const seconds = Math.floor(time % 60);
			return `${minutes}:${seconds.toString().padStart(2, '0')}`;
		};

		// Expose video methods to parent component
		useImperativeHandle(ref, () => ({
			get currentTime() {
				return videoRef.current?.currentTime || 0;
			},
			get duration() {
				return videoRef.current?.duration || 0;
			},
			play: async () => {
				if (videoRef.current) {
					await videoRef.current.play();
				}
			},
			pause: () => {
				if (videoRef.current) {
					videoRef.current.pause();
				}
			},
			setCurrentTime: async (time: number) => {
				if (videoRef.current && !isNaN(time) && isFinite(time)) {
					// Only set if the video is ready
					if (videoRef.current.readyState >= 2) {
						videoRef.current.currentTime = time;
						setCurrentTime(time);
					} else {
						// Wait for video to be ready
						return new Promise<void>((resolve) => {
							const handleCanPlay = () => {
								videoRef.current!.currentTime = time;
								setCurrentTime(time);
								videoRef.current!.removeEventListener('canplay', handleCanPlay);
								resolve();
							};
							videoRef.current!.addEventListener('canplay', handleCanPlay);
						});
					}
				}
			},
		}));

		return (
			<S.Base>
				<S.VideoWrapper onClick={togglePlayPause}>
					{isLoading && (
						<S.Loading>
							<S.LoadingContainer>
								<Loader width={24} height={24} />
								<S.LoadingText>loading...</S.LoadingText>
							</S.LoadingContainer>
						</S.Loading>
					)}
					<video
						ref={videoRef}
						src={src}
						onLoadStart={handleLoadStart}
						onCanPlay={handleCanPlay}
						onEnded={handleEnded}
						onTimeUpdate={handleTimeUpdate}
						onPlay={handlePlay}
						onPause={handlePause}
						onError={handleError}
						style={{
							width: '100%',
							height: 'auto',
							maxHeight: '100%',
							objectFit: 'contain',
							display: isLoading ? 'none' : 'block',
						}}
						controls={false}
					/>
				</S.VideoWrapper>

				{!isLoading && (
					<S.ControlsBar>
						<S.PlayPauseButton onClick={togglePlayPause}>
							<Icon
								type={isPlaying ? IconType.PAUSE : IconType.PLAY}
								width={24}
								height={24}
								color={unthemed.gray[1000]}
							/>
						</S.PlayPauseButton>

						<S.TimelineContainer>
							<S.Timeline
								type="range"
								min={0}
								max={duration || 0}
								value={currentTime}
								onChange={handleTimelineChange}
								onMouseDown={() => setIsDragging(true)}
								onMouseUp={() => setIsDragging(false)}
							/>
						</S.TimelineContainer>

						<S.TimeDisplay>
							{formatTime(currentTime)} / {formatTime(duration)}
						</S.TimeDisplay>

						<S.VolumeContainer data-volume-controls>
							<S.VolumeIcon onClick={toggleVolumeSlider}>
								<Icon
									type={volume === 0 ? IconType.VOLUME_MUTE : IconType.VOLUME_UP}
									width={16}
									height={16}
									color={unthemed.gray[100]}
								/>
							</S.VolumeIcon>
							{showVolumeSlider && (
								<S.VolumeSliderContainer>
									<S.VolumeSlider
										type="range"
										min={0}
										max={1}
										step={0.1}
										value={volume}
										onChange={handleVolumeChange}
									/>
								</S.VolumeSliderContainer>
							)}
						</S.VolumeContainer>

						<S.FullscreenButton onClick={toggleFullscreen}>
							<Icon type={IconType.FULLSCREEN} width={16} height={16} color={unthemed.gray[100]} />
						</S.FullscreenButton>
					</S.ControlsBar>
				)}
			</S.Base>
		);
	}
);

export default VideoPlayer;
